import { clamp, uniq } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

import { MODELING_CONST } from '@/constant';
import {
  useModelingListCountQuery,
  useModelingListQuery,
  useTypedSearchParams,
} from '@/hook';
import { formatDate } from '@/package';
import type { Modeling, ModelingListSearchParams } from '@/type';
import { calculateTrainingTime } from '@/util';

import ModelingListContext from './context';
import type { ModelingListContextValue } from './type';

const INIT_STATE = {
  count: {
    loaded: 0,
    total: 0,
  },
  page: {
    current: 1,
    total: 1,
  },
};

const ModelingListContextProvider = ({ children }: React.PropsWithChildren) => {
  const [count, setCount] = useState(INIT_STATE.count);
  const [page, setPage] = useState(INIT_STATE.page);
  const { get, set, flush, getAll } =
    useTypedSearchParams<ModelingListSearchParams>();
  const offset = Number(get('modelingOffset') ?? 0);
  const limit = Number(get('modelingLimit') ?? 10);
  const modelingStatus = getAll('modelingStatus') as Modeling['status'][];
  const allModelingStatus: Modeling['status'][] = Object.values(
    MODELING_CONST.MODELING_STATUS,
  );
  const isAll =
    modelingStatus.length === 0 ||
    allModelingStatus.every(status => modelingStatus.includes(status));

  const calcModelingStatus = isAll
    ? allModelingStatus
    : (modelingStatus as Modeling['status'][]);

  const {
    data: modedlingListData,
    isLoading: isListLoading,
    refetch,
  } = useModelingListQuery({
    limit,
    offset,
    versionType: 'RootModel',
    status: calcModelingStatus,
  });
  const { data: totalCount } = useModelingListCountQuery({
    limit,
    offset,
    versionType: 'RootModel',
    status: calcModelingStatus,
  });
  const list = modedlingListData ?? [];
  const [selectedIDList, setSelectedIDList] = useState([] as Modeling['id'][]);
  const [isAllSelected, isIndeterminated] = useMemo(() => {
    const listLength = list.length;
    const selectedList = selectedIDList.filter(
      id => list.findIndex(i => i.id === id) >= 0,
    );
    const isAllSelected = selectedList.length === listLength;
    const isIndeterminated = !isAllSelected && selectedList.length > 0;

    return [isAllSelected, isIndeterminated];
  }, [list, selectedIDList]);
  const [progressMap, setProgressMap] = useState(
    {} as {
      [K in Modeling['id']]: number;
    },
  );
  const renderedList = useMemo(() => {
    const renderedList = list.map(raw => {
      const dataSplit =
        raw.splitMode === 'None'
          ? '-'
          : raw.splitMode === 'Auto'
            ? 'Auto'
            : 'User';
      const { hours, minutes, seconds } = calculateTrainingTime(raw);
      const trainingTime =
        hours < 0 || minutes < 0 || seconds < 0
          ? '-'
          : `${hours.toString().padStart(2, '0')}h ${minutes
              .toString()
              .padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;

      return {
        id: raw.id,
        status: raw.status,
        progress: progressMap[raw.id] ?? 0,
        sensorType: raw.sensorType,
        modelName: raw.name,
        trainingRun: raw.trainingRun,
        function: raw.modelFunction,
        dataSplit,
        trainingTime,
        createdTime: formatDate(raw.createdAt),
        deployment: null,
        remove: null,

        raw,
      };
    });

    return renderedList;
  }, [list, progressMap, selectedIDList]);

  const clear = () => {
    setCount(INIT_STATE.count);
    setPage(INIT_STATE.page);
  };
  const nextPage = () => movePage(page.current + 1);
  const movePage = (newPageNumber: number) => {
    newPageNumber = clamp(newPageNumber, 1, page.total);

    if (newPageNumber === page.current) return;

    set('modelingOffset', `${newPageNumber - 1 * limit}`);
  };
  const prevPage = () => movePage(page.current - 1);
  const refresh = () => refetch().then(() => {});
  const select = (id: Modeling['id']) =>
    setSelectedIDList(prev => uniq(prev.concat(id)));
  const selectAll = () => setSelectedIDList(list.map(i => i.id));
  const setProgress = (id: Modeling['id'], progressValue: number) =>
    setProgressEntryList([[id, progressValue]]);
  const setProgressEntryList = (entryList: [Modeling['id'], number][]) => {
    const addedMap = Object.fromEntries(entryList);

    setProgressMap(p => ({ ...p, ...addedMap }));
  };

  useEffect(() => {
    setCount({ loaded: list.length, total: totalCount ?? 0 });
  }, [list.length, totalCount]);

  useEffect(() => {
    const newPage = Math.floor(offset / (limit ?? 1)) + 1;
    const totalPage = Math.ceil((totalCount ?? 0) / (limit ?? 1));

    setPage(p => ({ ...p, current: newPage, total: totalPage }));
  }, [offset, limit, totalCount]);

  useEffect(() => {
    flush();
  }, [page.current]);

  useEffect(() => {
    return () => clear();
  }, []);

  return (
    <ModelingListContext.Provider<ModelingListContextValue>
      value={{
        count,
        isAllSelected,
        isIndeterminated,
        isListLoading,
        list,
        page,
        renderedList,
        selectedIDList,

        clear,
        nextPage,
        movePage,
        prevPage,
        refresh,
        select,
        selectAll,
        setProgress,
        setProgressEntryList,
      }}
    >
      {children}
    </ModelingListContext.Provider>
  );
};

export default ModelingListContextProvider;
